import React, { useState, useRef, useEffect } from 'react';
import FormModal from 'components/shared/modal/FormModal';
import Button, {
  BUTTON_TYPES,
  BUTTON_COLORS,
} from 'components/shared/button/Button';

import './SignaturePadModal.css';

const SignaturePadModal = ({
  show,
  onClose,
  onSave,
  mode = 'draw',
  initialImageUrl = null,
}) => {
  const [currentMode, setCurrentMode] = useState(mode);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [fontSize, setFontSize] = useState(48);
  const [fontFamily, setFontFamily] = useState('Arial');
  const canvasRef = useRef(null);
  const textCanvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) {
      // Reset state when modal closes
      setUploadedImage(null);
      setCurrentMode(mode);
      setIsDrawing(false);
      setTextInput('');
      setFontSize(48);
      setFontFamily('Arial');
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      if (textCanvasRef.current) {
        const canvas = textCanvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      // Update mode when modal opens with a new mode
      setCurrentMode(mode);
    }
  }, [show, mode]);

  // Initialize canvas when modal opens
  useEffect(() => {
    if (show && currentMode === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas size
      canvas.width = 600;
      canvas.height = 300;

      // Set white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Set drawing style
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Load initial image if provided (for editing)
      if (initialImageUrl) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          // Calculate scaling to fit image in canvas while maintaining aspect ratio
          const scale = Math.min(
            canvas.width / img.width,
            canvas.height / img.height
          );
          const x = (canvas.width - img.width * scale) / 2;
          const y = (canvas.height - img.height * scale) / 2;

          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        };
        img.onerror = () => {
          console.error('Failed to load initial image');
        };
        img.src = initialImageUrl;
      }
    }
  }, [show, currentMode, initialImageUrl]);

  // Initialize text canvas when switching to text mode
  useEffect(() => {
    if (show && currentMode === 'text' && textCanvasRef.current) {
      const canvas = textCanvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas size
      canvas.width = 600;
      canvas.height = 200;

      // Set white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [show, currentMode]);

  // Render text to canvas when text input changes
  useEffect(() => {
    if (currentMode === 'text' && textCanvasRef.current) {
      const canvas = textCanvasRef.current;
      const ctx = canvas.getContext('2d');

      // Ensure canvas is initialized
      if (canvas.width === 0 || canvas.height === 0) {
        canvas.width = 600;
        canvas.height = 200;
      }

      // Clear and set white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (textInput.trim()) {
        // Set text style
        ctx.fillStyle = '#000000';
        ctx.font = `${fontSize}px ${fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Draw text centered
        const lines = textInput.split('\n');
        const lineHeight = fontSize * 1.2;
        const startY = (canvas.height - (lines.length - 1) * lineHeight) / 2;

        lines.forEach((line, index) => {
          ctx.fillText(line, canvas.width / 2, startY + index * lineHeight);
        });
      }
    }
  }, [currentMode, textInput, fontSize, fontFamily]);

  const handleClear = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    if (textCanvasRef.current) {
      const canvas = textCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    setUploadedImage(null);
    setTextInput('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    if (currentMode !== 'draw' || !canvasRef.current) return;

    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);

    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);

    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    if (!isDrawing) return;

    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
    }
  };

  const handleSave = () => {
    let dataUrl = null;

    if (currentMode === 'draw' && canvasRef.current) {
      // Check if canvas is empty (all white)
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      let isEmpty = true;

      // Check if there's any non-white pixel
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255) {
          isEmpty = false;
          break;
        }
      }

      if (isEmpty && !initialImageUrl) {
        return; // Don't save empty signature (unless we're editing an existing one)
      }

      dataUrl = canvas.toDataURL('image/png');
    } else if (currentMode === 'upload' && (uploadedImage || initialImageUrl)) {
      dataUrl = uploadedImage || initialImageUrl;
    } else if (
      currentMode === 'text' &&
      textCanvasRef.current &&
      textInput.trim()
    ) {
      dataUrl = textCanvasRef.current.toDataURL('image/png');
    }

    if (dataUrl) {
      onSave(dataUrl);
      handleClear();
      // Don't call onClose() here - let the parent component handle closing
      // onClose() should only be called when user explicitly cancels
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(png|jpg|jpeg)$/)) {
      alert('Please select a PNG or JPG image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image is too large. Maximum 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const isEmpty = () => {
    if (currentMode === 'draw') {
      if (!canvasRef.current) return !initialImageUrl;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Check if there's any non-white pixel
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255) {
          return false;
        }
      }
      return !initialImageUrl;
    } else if (currentMode === 'upload') {
      return !uploadedImage && !initialImageUrl;
    } else if (currentMode === 'text') {
      return !textInput.trim();
    }
    return true;
  };

  return (
    <FormModal
      show={show}
      onClose={onClose}
      title="Add Signature"
      className="signature-pad-modal"
    >
      <div className="signature-pad-container">
        {/* Mode Toggle */}
        <div className="signature-mode-toggle">
          <button
            className={`mode-button ${currentMode === 'draw' ? 'active' : ''}`}
            onClick={() => setCurrentMode('draw')}
          >
            Draw
          </button>
          <button
            className={`mode-button ${
              currentMode === 'upload' ? 'active' : ''
            }`}
            onClick={() => setCurrentMode('upload')}
          >
            Upload Image
          </button>
          <button
            className={`mode-button ${currentMode === 'text' ? 'active' : ''}`}
            onClick={() => setCurrentMode('text')}
          >
            Type Text
          </button>
        </div>

        {/* Draw Mode */}
        {currentMode === 'draw' && (
          <div className="signature-canvas-container">
            <canvas
              ref={canvasRef}
              className="signature-canvas"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
        )}

        {/* Upload Mode */}
        {currentMode === 'upload' && (
          <div className="signature-upload-container">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {uploadedImage || initialImageUrl ? (
              <div className="signature-preview">
                <img
                  src={uploadedImage || initialImageUrl}
                  alt="Uploaded signature"
                  crossOrigin="anonymous"
                />
              </div>
            ) : (
              <div
                className="signature-upload-placeholder"
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.click();
                  }
                }}
              >
                <p>Click to select an image</p>
                <p className="hint">PNG or JPG, maximum 5MB</p>
              </div>
            )}
          </div>
        )}

        {/* Text Mode */}
        {currentMode === 'text' && (
          <div className="signature-text-container">
            <div className="signature-text-controls">
              <div className="text-control-group">
                <label htmlFor="font-size">Font Size:</label>
                <input
                  id="font-size"
                  type="number"
                  min="12"
                  max="120"
                  value={fontSize}
                  onChange={(e) => setFontSize(parseInt(e.target.value) || 48)}
                  className="text-control-input"
                />
              </div>
              <div className="text-control-group">
                <label htmlFor="font-family">Font:</label>
                <select
                  id="font-family"
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  className="text-control-input"
                >
                  <option value="Arial">Arial</option>
                  <option value="Times New Roman">Times New Roman</option>
                </select>
              </div>
            </div>
            <div className="signature-text-input-container">
              <textarea
                className="signature-text-input"
                placeholder="Type your text here..."
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                rows={4}
              />
            </div>
            <div className="signature-text-preview-container">
              <canvas ref={textCanvasRef} className="signature-text-canvas" />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="signature-pad-actions">
          <Button
            type={BUTTON_TYPES.SECONDARY}
            color={BUTTON_COLORS.GRAY}
            label="Clear"
            onClick={handleClear}
          />
          <Button
            type={BUTTON_TYPES.PRIMARY}
            color={BUTTON_COLORS.GREEN}
            label="Save Signature"
            onClick={handleSave}
            disabled={isEmpty()}
          />
        </div>
      </div>
    </FormModal>
  );
};

export default SignaturePadModal;
